import {Component, computed, inject} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {takeUntilDestroyed, rxResource, toSignal} from '@angular/core/rxjs-interop';
import {ProviderTenderControllerService, TenderPhaseType} from '@frontend/shared-api';
import {APP_ROUTES} from '../../../app.routes';
import {RfdTenderWizardComponent} from './rfd/rfd-wizard/rfd-tender-wizard.component';
import {TenderQuestionnaireWizardComponent} from './components/questionnaire-wizard/tender-questionnaire-wizard.component';
import {RfqTenderWizardComponent} from './rfq/rfq-wizard/rfq-tender-wizard.component';
import {RfcTenderWizardComponent} from './rfc/rfc-wizard/rfc-tender-wizard.component';
import {TenderPhaseUtils} from '../../../phase/tender-phase.util';
import {LoadingComponent} from '../../../shared/components/loading/loading.component';
import {TENDER_ACTION_QUERY_PARAM, TenderAction} from './tender-wizard.types';

@Component({
  imports: [
    RfdTenderWizardComponent,
    TenderQuestionnaireWizardComponent,
    RfqTenderWizardComponent,
    RfcTenderWizardComponent,
    LoadingComponent,
  ],
  templateUrl: './tender-wizard.component.html',
})
export class TenderWizardComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly providerTenderService = inject(ProviderTenderControllerService);

  private readonly params = toSignal(this.route.paramMap);
  private readonly queryParams = toSignal(this.route.queryParamMap);

  protected readonly tenderId = computed(() => {
    const idStr = this.params()?.get('tenderId');
    const id = Number(idStr);

    if (!idStr || isNaN(id)) {
      throw new Error(`Fatal failure: tenderId "${idStr}" is missing or invalid`);
    }

    return id;
  });

  protected readonly tenderPhase = computed(() => {
    const phase = this.params()?.get('phase');
    return phase ? TenderPhaseUtils.mapPhaseStringToEnum(phase) : null;
  });

  protected readonly openDecision = computed(
    () => this.queryParams()?.get(TENDER_ACTION_QUERY_PARAM) === TenderAction.DECLINE,
  );

  protected readonly isRfi = computed(() => this.tenderPhase() === TenderPhaseType.RFI);
  protected readonly isRfp = computed(() => this.tenderPhase() === TenderPhaseType.RFP);
  protected readonly isRfd = computed(() => this.tenderPhase() === TenderPhaseType.RFD);
  protected readonly isRfq = computed(() => this.tenderPhase() === TenderPhaseType.RFQ);
  protected readonly isRfc = computed(() => this.tenderPhase() === TenderPhaseType.RFC);

  protected readonly tenderDetailsResource = rxResource({
    params: () => {
      const tenderId = this.tenderId();
      const tenderPhase = this.tenderPhase();
      if (!tenderId || !tenderPhase) return undefined;
      return {tenderId, tenderPhase};
    },
    stream: ({params: request}) => this.providerTenderService.getTenderDetails(request!),
  });

  constructor() {
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      const tenderId = params.get('tenderId');
      const phase = params.get('phase');
      if (!tenderId || !phase || !TenderPhaseUtils.mapPhaseStringToEnum(phase)) {
        console.error('Invalid tender phase:', phase);
        this.router.navigate([APP_ROUTES.PROVIDER_TENDERS]);
      }
    });
  }

  protected onTenderPhaseAccepted(): void {
    this.tenderDetailsResource.reload();
  }
}
